import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme/theme';
import './index.css';
import { NotificationProvider } from './context/NotificationContext';
import { NotificationProvider as InAppNotificationProvider } from './contexts/NotificationContext';

// Import pages and components
import Landing from './pages/Landing';
import LOAPage from './components/LOA/LOAPage';
import LOAPDFWrapper from './components/LOA/LOAPDFWrapper';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import ProtectedRoute from './components/ProtectedRoute';
import ArchivedRecords from './pages/ArchivedRecords';
import { AuthProvider } from './context/AuthContext';
import { ClientProvider } from './context/ClientContext';

// Utilities section
import UtilitiesLayout from './components/layout/UtilitiesLayout';
// Using UtilityDashboard as the main dashboard
import UtilityDashboard from './pages/UtilityDashboard';
import SubBrokerDashboard from './pages/SubBrokerDashboard';
import SubBrokerDashboardView from './pages/SubBrokerDashboard/SubBrokerDashboardView';
import DashboardRouter from './pages/DashboardRouter';
import Leads from './pages/Leads';
import Clients from './pages/utilities/Clients';
import Properties from './pages/utilities/Properties';
// Services import removed - functionality consolidated with Contracts
import Contracts from './pages/utilities/Contracts'; // New contracts page
import ContractForm from './pages/utilities/ContractForm'; // New contract form
import PropertyDetails from './pages/utilities/PropertyDetails';
import ClientDetails from './pages/utilities/ClientDetails';
import HelpPage from './pages/Help';

// Quotes section
import QuoteForm from './pages/Quotes/QuoteForm';
import QuoteViewer from './pages/Quotes/QuoteViewer';
import QuoteListPage from './pages/Quotes/QuoteList';
import CompanyChecker from './pages/CompanyChecker';

// Admin components
import AdminPanel from './pages/Admin/AdminPanel';
import { UserManagement } from './pages/Admin/UserManagement';
import EmailConfig from './pages/Admin/EmailConfig';
import EmailTemplates from './pages/Admin/EmailTemplates';
import Lookups from './pages/Admin/Lookups';
import AdminNotifications from './pages/Admin/Notifications';
import Suppliers from './pages/utilities/Suppliers';
import EmailLogs from './pages/Admin/EmailLogs';
import SubscribersPage from './pages/marketing/Subscribers';
import BlogsPage from './pages/marketing/Blogs';
import CampaignsPage from './pages/marketing/Campaigns';
import MarketingLayout from './pages/marketing/MarketingLayout';
import { useAuth } from './context/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enGB from 'date-fns/locale/en-GB';
import ErrorBoundary from './components/ErrorBoundary';
import { setupAxiosErrorInterceptor } from './utils/axiosErrorInterceptor';
import { setupApiErrorLogging } from './utils/apiErrorLogger';
import ErrorTestPage from './pages/ErrorTestPage';
import NotificationTest from './components/test/NotificationTest';

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/utilities" replace /> : <Landing />
      } />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/change-password" element={
        <ProtectedRoute allowPasswordChange><ChangePassword /></ProtectedRoute>
      } />
      <Route path="/archived-records" element={
        <ProtectedRoute><ArchivedRecords /></ProtectedRoute>
      } />
      <Route path="/loa/:token" element={<LOAPage />} />
      <Route path="/loa-pdf" element={<LOAPDFWrapper />} />

      {/* Root redirect */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to="/utilities" replace /> : <Navigate to="/login" replace />
      } />

      {/* Protected routes */}
      <Route path="/utilities" element={
        <ProtectedRoute>
          <UtilitiesLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardRouter />} />
        <Route path="dashboard" element={<DashboardRouter />} />
        <Route path="sub-broker-view/:subBrokerId" element={<SubBrokerDashboardView />} />
        <Route path="leads" element={<Leads />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetails />} />
        <Route path="clients/:id/properties/:propertyId" element={<PropertyDetails />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:propertyId" element={<PropertyDetails />} />
        {/* Services route removed - functionality consolidated with Contracts */}
        <Route path="contracts" element={<Contracts />} /> {/* New contracts page */}
        <Route path="contracts/new" element={<ContractForm />} /> {/* New contract form */}
        <Route path="contracts/:id" element={<ContractForm />} /> {/* View contract details */}
        <Route path="contracts/:id/edit" element={<ContractForm />} /> {/* Edit existing contract */}
        <Route path="quotes" element={<QuoteListPage />} />
        <Route path="quotes/new" element={<QuoteForm />} />
        <Route path="quotes/:id/edit" element={<QuoteForm />} />
        <Route path="quotes/:id/view" element={<QuoteViewer />} />
        <Route path="company-checker" element={<CompanyChecker />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="marketing" element={<MarketingLayout />}>
          <Route index element={<Navigate to="/utilities/marketing/subscribers" replace />} />
          <Route path="subscribers" element={<SubscribersPage />} />
          <Route path="blogs" element={<BlogsPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
        </Route>
        <Route path="help" element={<HelpPage />} />
        <Route path="help/:slug" element={<HelpPage />} />
        <Route path="error-test" element={<ErrorTestPage />} />
        <Route path="notification-test" element={<NotificationTest />} />
      </Route>
      
      {/* Admin routes - separated from UtilitiesLayout to prevent styling conflicts */}
      <Route path="/utilities/admin" element={
        <ProtectedRoute adminOnly>
          <AdminPanel />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/utilities/admin/dashboard" replace />} />
        <Route path="dashboard" element={null} /> {/* No component needed as AdminPanel handles layout */}
        <Route path="lookups" element={<Lookups />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="email-config" element={<EmailConfig />} />
        <Route path="email-templates" element={<EmailTemplates />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="email-logs" element={<EmailLogs />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/utilities/dashboard" replace />} />
    </Routes>
  );
}

const queryClient = new QueryClient();

function App() {
  useEffect(() => {
    // Set up API error logging (new centralized system)
    setupApiErrorLogging();
    
    // Set up legacy axios interceptor (for backward compatibility)
    const removeAxiosInterceptor = setupAxiosErrorInterceptor();
    
    // Note: Global error handlers are now set up in logger.ts
    // No need to duplicate them here
    
    // Clean up function
    return () => {
      removeAxiosInterceptor();
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ClientProvider>
          <InAppNotificationProvider>
          <NotificationProvider>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <SnackbarProvider maxSnack={3}>
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                  </SnackbarProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </NotificationProvider>
          </InAppNotificationProvider>
        </ClientProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;